import React from "react";
import { BrandProject } from "./components/constants";
import { ReactComponent as ClockIcon } from "../../assets/svg/clock.svg";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"; // Import MUI Calendar icon
import CameraAltIcon from "@mui/icons-material/CameraAlt"; // Import MUI camera icon
import CancelIcon from "@mui/icons-material/Cancel";
import { IconCheck } from "../../utils/icons";
import { isAM, isBrand, isInternal, isPostProd, isProTeam, isRMN } from "../../utils";
import { formatFilmDayList, toDayDate, toDayList } from "../../utils/filmWindow";
import { withInstanceLabel } from "../../hooks/useChannelGroups";

export interface SharedProcessProject {
    id: number;
    title?: string;
}

/**
 * A human sentence naming the OTHER projects that share an expert-selection
 * process (brand_order_pros), e.g. "2 other projects in this order (Foo, Bar)".
 * Returns "" when the process isn't shared. Keeps the shared-casting wording
 * identical across every casting screen — see the useProcessSiblings hook, which
 * is the single source of the sibling set.
 */
export const describeSharedSiblings = (
    otherProjects: SharedProcessProject[] | undefined,
): string => {
    if (!otherProjects || otherProjects.length === 0) return "";
    // Label every sibling (fallback to its id) so the count and the listed names
    // always agree — a missing title must not drop a project from the list.
    const names = otherProjects.map((p) => p.title || `#${p.id}`).join(", ");
    const n = otherProjects.length;
    const noun = n === 1 ? "1 other project" : `${n} other projects`;
    return `${noun} in this order (${names})`;
};

export const PILL_SX = {
    color: "var(--text-2)",
    fontFamily: "CircularStd",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 450,
    lineHeight: "normal",
    borderRadius: "24px",
    backgroundColor: "var(--surface-2)",
    padding: 1
};

export const CHANNEL_PILL_SX = {
    ...PILL_SX,
    // --track (not --surface-2) so the channel pill stays a step darker than the
    // default PILL_SX pill it sits next to, as #E8E8E8 did against #F4F4F4.
    backgroundColor: "var(--track)",
    fontSize: "10px",
    height: "22px",
    borderRadius: "16px",
    padding: 0,
    "& .MuiChip-label": {
        paddingLeft: "8px",
        paddingRight: "8px"
    }
};

export const BRAND_PROJECT_BRIEF_STATUSES = {
    BRIEF_REVIEW_NEEDED: "Brief Review",
    DENIED: "Brief Denied",
    APPROVED: "Brief Approved"
};

export const BRAND_PROJECT_STATUSES: any = {
    PENDING: "Pending Approval",
    BRIEF_PENDING: "Waiting For Brief",
    CANCELLED: "Cancelled",
    BRIEF_REVIEW_NEEDED: "Brief Review",
    BRIEF_DENIED: "Brief Denied",
    EXPERT_PROPOSALS_NEEDED: "Expert Proposals",
    EXPERTS_REVIEW_NEEDED: "Experts Proposal Review",
    EXPERTS_REVIEW_DENIED: "Experts Proposal Review Denied",
    EXPERTS_SELECTION_NEEDED: "Experts Selection",
    SCHEDULING: "Scheduling",
    PRE_PRODUCTION: "Pre-Production",
    FILMING: "Filming",
    EDITING: "Editing",
    VERSION_REVIEW_NEEDED: "Video Review",
    FINAL_DELIVERED: "Final Delivered"
};

// Mirrors BRAND_PROJECT_INTERNAL_STATUSES in the API (brandProjects.js).
// Internal-only sub-status; never shown to brand users.
export const BRAND_PROJECT_INTERNAL_STATUSES = {
    READY_FOR_CLIENT: "Ready For Client",
    SENT_TO_CLIENT: "Sent To Client",
    FEEDBACK_READY_FOR_PRODUCER: "Feedback Ready For Producer",
    FEEDBACK_READY_FOR_POST: "Feedback Ready For Post",
    POST_CHECK: "Post Check",
    POST_APPROVED: "Post Approved",
    CLIENT_DELIVERED: "Client Delivered",
    ARCHIVED: "Archived"
};

export const INTERNAL_STATUS_OPTIONS = Object.values(BRAND_PROJECT_INTERNAL_STATUSES).map((value) => ({
    label: value,
    value
}));

const INTERNAL_STATUS_VALUES = new Set<string>(Object.values(BRAND_PROJECT_INTERNAL_STATUSES));

export const isInternalStatus = (value: string) => INTERNAL_STATUS_VALUES.has(value);

// Options for a deliverable's internal-status picker. A value this build no
// longer offers (a retired status still on the row, or a newer API status
// this portal build predates — the API always deploys first) stays in the
// list while it is that deliverable's CURRENT value; otherwise MUI's Select
// gets a value with no matching MenuItem, renders blank and warns
// out-of-range.
export const internalStatusOptionsFor = (current?: string | null) =>
    current && !isInternalStatus(current)
        ? [...INTERNAL_STATUS_OPTIONS, { label: current, value: current }]
        : INTERNAL_STATUS_OPTIONS;

// The lavender is deliberate: it marks internal-only state, so it must not be
// confused with the green/red delivery statuses or the gold brand chips. Dark
// mode SWAPS the pair's roles rather than inventing a new purple — the light
// background becomes the text, the light text becomes a translucent wash (the
// same construction as --up-bg / --down-bg). No new colors, light untouched,
// and 10.7:1 contrast instead of the light chip's bright blob on dark navy.
export const INTERNAL_STATUS_CHIP_STYLE = {
    color: "#5B4B8A",
    backgroundColor: "#EFEAFB",
    '[data-theme="dark"] &': {
        color: "#EFEAFB",
        backgroundColor: "rgba(91,75,138,.35)"
    }
};

// Label channel entries the way useChannelGroups does ("PDP", "PDP #2",
// "PDP #2 · Holiday"): duplicates numbered in array order, the instance label
// beside it. Derived content_channels entries are already sorted by instance
// id server-side, so ordering matches everywhere.
export const labelChannelEntries = <T extends {name?: string; instance_label?: string | null}>(
    entries: T[]
): Array<T & {label: string}> => {
    const counts = new Map<string, number>();
    return entries.map((entry) => {
        const name = entry.name || "Channel";
        const n = (counts.get(name) || 0) + 1;
        counts.set(name, n);
        return {...entry, label: withInstanceLabel(n > 1 ? `${name} #${n}` : name, entry.instance_label)};
    });
};

export const BRAND_PROJECT_TDC_SHIPPING_STATUSES = {
    PENDING: "Pending",
    EXCEPTION: "Exception",
    SHIPPED: "Shipped",
    RECEIVED: "Received"
};

export const BRAND_PROJECT_EXPERT_SHIPPING_STATUSES = {
    PENDING: "Pending",
    EXCEPTION: "Exception",
    SHIPPED: "Shipped",
    RECEIVED: "Received"
};

export const getExpertStatusStyle = (project: BrandProject): { color: string; tooltip: string } => {
    const {status} = project;
    const confirmedStatuses = [
        BRAND_PROJECT_STATUSES.PRE_PRODUCTION,
        BRAND_PROJECT_STATUSES.FILMING,
        BRAND_PROJECT_STATUSES.EDITING,
        BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED,
        BRAND_PROJECT_STATUSES.FINAL_DELIVERED
    ];

    if (confirmedStatuses.includes(status) && project.film_date) {
        return { color: "#4CAF50", tooltip: "Expert and film date confirmed" };
    }
    if (status === BRAND_PROJECT_STATUSES.SCHEDULING) {
        return { color: "#2196F3", tooltip: "Experts ranked by client" };
    }
    if (status === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED) {
        return { color: "#FFC107", tooltip: "Awaiting client to rank experts" };
    }
    if (
        status === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED ||
        status === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED
    ) {
        return { color: "#FF9800", tooltip: "Expert proposals awaiting AM approval" };
    }
    const proposedCount = project.project_pros?.length ?? project.project_pros_count ?? 0;
    if (status === BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED && proposedCount > 0) {
        return { color: "#EB3E33", tooltip: "Experts proposed, waiting on opt-in/out or submission for review" };
    }
    return { color: "#000000", tooltip: "No experts proposed yet" };
};

export const getModalType = (projectStatus: string | undefined): "firstTime" | "alreadySubmitted" => {
    // Submit For Review is disabled from Experts Selection onward (see
    // submitDisabledReason on the expert selection page), so the only
    // reachable "resubmit" states are the two review ones.
    const statusesWithSubmittedExperts = [
        BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED,
        BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED
    ];

    return statusesWithSubmittedExperts.includes(projectStatus || "") ? "alreadySubmitted" : "firstTime";
};

export const formatKeyProjectDate = (value: string | undefined): string => {
    if (!value) return "Unavailable";
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "Unavailable";
        return d.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric"
        });
    } catch {
        return "Unavailable";
    }
};

export type ProjectWithDeliveredDate = {
    id?: number;
    projectFiles?: Array<{ id?: number; master?: boolean; created_at?: string; video_approved_at?: string }>;
};

export function getProjectDatesForDisplay(p: ProjectWithDeliveredDate | null | undefined): {
    deliveredDate: string | null;
    approvalDate: string | null;
} {
    const mainVideo = p?.projectFiles?.find((f) => f.master === true) ?? p?.projectFiles?.[0];
    if (!mainVideo) {
        return { deliveredDate: null, approvalDate: null };
    }
    const delivered = mainVideo?.created_at ?? (mainVideo as { createdAt?: string }).createdAt;
    const approved = mainVideo?.video_approved_at;
    const deliveredStr =
        delivered && formatKeyProjectDate(delivered) !== "Unavailable" ? formatKeyProjectDate(delivered) : null;
    const approvedStr =
        approved && formatKeyProjectDate(approved) !== "Unavailable" ? formatKeyProjectDate(approved) : null;
    return { deliveredDate: deliveredStr, approvalDate: approvedStr };
}

export function getVideoDatesForDisplay(
    video: { created_at?: string; createdAt?: string; video_approved_at?: string } | null | undefined
): { deliveredDate: string | null; approvalDate: string | null } {
    if (!video) return { deliveredDate: null, approvalDate: null };
    const delivered = video.created_at ?? video.createdAt;
    const approved = video.video_approved_at;
    const deliveredStr =
        delivered && formatKeyProjectDate(delivered) !== "Unavailable" ? formatKeyProjectDate(delivered) : null;
    const approvedStr =
        approved && formatKeyProjectDate(approved) !== "Unavailable" ? formatKeyProjectDate(approved) : null;
    return { deliveredDate: deliveredStr, approvalDate: approvedStr };
}

export const BRAND_PROJECT_STATUS_STYLES: any = {
    PENDING: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    BRIEF_PENDING: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    CANCELLED: {
        icon: <CancelIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />,
        color: "#EB3E33",
        textColor: "#FFFFFF"
    },
    BRIEF_REVIEW_NEEDED: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    BRIEF_DENIED: {
        icon: <CancelIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />,
        color: "#EB3E33",
        textColor: "#FFFFFF"
    },
    EXPERT_PROPOSALS_NEEDED: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    EXPERTS_REVIEW_NEEDED: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    EXPERTS_REVIEW_DENIED: {
        icon: <CancelIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />,
        color: "#EB3E33",
        textColor: "#FFFFFF"
    },
    EXPERTS_SELECTION_NEEDED: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    SCHEDULING: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    // These two carried an icon but no color/textColor, so the chip fell back
    // to MUI's default grey — which reads as a light slab on the dark canvas
    // while every sibling status renders as a gold-tint chip. Matched to the
    // other in-progress statuses.
    PRE_PRODUCTION: {
        icon: <CalendarTodayIcon sx={{ fontSize: 16, color: "var(--accent-text)" }} />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    FILMING: {
        icon: <CameraAltIcon sx={{ fontSize: 16, color: "var(--accent-text)" }} />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    EDITING: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    VERSION_REVIEW_NEEDED: {
        icon: <ClockIcon className="w-4 h-4" />,
        color: "var(--tint)",
        textColor: "var(--accent-text)"
    },
    FINAL_DELIVERED: {
        icon: <IconCheck stroke="#1FCE7A" width={24} height={24} />,
        color: "#34AC881A",
        textColor: "#1FCE7A"
    }
};

export const sortBrandsByRmn = (data: BrandProject[]): BrandProject[] =>
    [...data].map((project: BrandProject) => {
        const sortedBrands = project?.brands?.sort((a, b) => {
            if (a.rmn && !b.rmn) return -1;
            if (!a.rmn && b.rmn) return 1;
            return 0;
        });
        project.brands = sortedBrands;
        return { ...project };
    });

export const getSpecialStatus = (project: BrandProject): string => {
    if (project.status !== BRAND_PROJECT_STATUSES.PRE_PRODUCTION || !project.film_date) {
        return project.status;
    }

    const today = new Date();
    // UTC-midnight day markers — compare as calendar days.
    const filmDate = toDayDate(project.film_date);
    const filmDateEnd = toDayDate(project.film_date_end) || filmDate;
    if (!filmDate || !filmDateEnd) return project.status;

    today.setHours(0, 0, 0, 0);

    if (today.getTime() >= filmDate.getTime() && today.getTime() <= filmDateEnd.getTime()) {
        return BRAND_PROJECT_STATUSES.FILMING;
    }
    if (today.getTime() > filmDateEnd.getTime()) {
        return BRAND_PROJECT_STATUSES.EDITING;
    }

    return project.status;
};

export const updateExpertScoreInProjectPros = (projectPros: any[], userId: string, newScore: number): any[] => projectPros.map((pro) =>
        pro.userid === userId && pro.projects?.[0]
            ? {
                  ...pro,
                  projects: [
                      {
                          ...pro.projects[0],
                          BrandProjectPro: {
                              ...pro.projects[0].BrandProjectPro,
                              score: newScore
                          }
                      }
                  ]
              }
            : pro
    );

// Rate twin of updateExpertScoreInProjectPros: keeps the parent's projectPros
// state in step with the card's debounced rate save, so rate-gated UI (the
// opt-in send button) reacts as the user types instead of waiting for a
// refetch.
export const updateExpertRateInProjectPros = (projectPros: any[], userId: string | number, newRate: string | null): any[] => projectPros.map((pro) =>
        pro.userid === userId && pro.projects?.[0]
            ? {
                  ...pro,
                  projects: [
                      {
                          ...pro.projects[0],
                          BrandProjectPro: {
                              ...pro.projects[0].BrandProjectPro,
                              rate: newRate
                          }
                      }
                  ]
              }
            : pro
    );

export const calculateMaxPercentage = (projectPros: any[]): number => projectPros.reduce((max, pro) => Math.max(max, Number(pro.projects?.[0]?.BrandProjectPro?.score) || 0), 0);

export const formatContactDate = (iso: string): string | null => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
};

export const buildExpertAvailability = (confirmation: any, pivot: any): string => {
    const days = formatFilmDayList(toDayList(confirmation?.preferred_dates ?? pivot?.preferred_dates));
    if (days) return `Prefers ${days}`;
    const status = confirmation?.confirmation_status || pivot?.confirmation_status;
    const interested = confirmation?.interested ?? pivot?.interested;
    if (pivot?.internal_confirmed) return "Confirmed";
    if (status === "submitted") return "Availability shared";
    if (interested === false) return "Declined";
    if (interested === true) return "Interested";
    if (status) return status.charAt(0).toUpperCase() + status.slice(1);
    return "";
};

export const sortExpertsByScore = (experts: any[]) => [...experts].sort((a, b) => {
        const scoreA = parseFloat(a.projects?.[0]?.BrandProjectPro?.score || "0");
        const scoreB = parseFloat(b.projects?.[0]?.BrandProjectPro?.score || "0");
        return scoreB - scoreA;
    });

export const sortExpertsBySelection = (experts: any[]) => [...experts].sort((a, b) => {
        const statusA = a.projects?.[0]?.BrandProjectPro?.status || 999;
        const statusB = b.projects?.[0]?.BrandProjectPro?.status || 999;

        if (statusA !== 999 && statusB !== 999) {
            if (statusA === statusB) {
                const scoreA = parseFloat(a.projects?.[0]?.BrandProjectPro?.score || "0");
                const scoreB = parseFloat(b.projects?.[0]?.BrandProjectPro?.score || "0");
                return scoreB - scoreA;
            }
            return statusA - statusB;
        }

        if (statusA !== 999) return -1;
        if (statusB !== 999) return 1;

        const scoreA = parseFloat(a.projects?.[0]?.BrandProjectPro?.score || "0");
        const scoreB = parseFloat(b.projects?.[0]?.BrandProjectPro?.score || "0");
        return scoreB - scoreA;
    });

export const areExpertsApproved = (projectStatus: string | undefined): boolean => (
        projectStatus === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED ||
        projectStatus === BRAND_PROJECT_STATUSES.SCHEDULING ||
        projectStatus === BRAND_PROJECT_STATUSES.PRE_PRODUCTION ||
        projectStatus === BRAND_PROJECT_STATUSES.FILMING ||
        projectStatus === BRAND_PROJECT_STATUSES.EDITING ||
        projectStatus === BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED ||
        projectStatus === BRAND_PROJECT_STATUSES.FINAL_DELIVERED
    );

export const canAccessStatus = (project: BrandProject, status: string, role: string, _user?: any): boolean => {
    const isInternalUser = [
        "PRO_TEAM_ADMIN",
        "SALES_TEAM",
        "ADMIN",
        "ACCOUNT_MANAGER_ADMIN",
        "POST_PRODUCTION_ADMIN"
    ].includes(role);
    const isBrandUser = ["BRAND_ADMIN", "BRAND_MEMBER"].includes(role);

    const statusValue = status || project.status || "";

    if (statusValue === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED || statusValue === "Experts Selection") {
        return isInternalUser || areExpertsApproved(project.status);
    }

    if (
        statusValue === BRAND_PROJECT_STATUSES.SCHEDULING ||
        statusValue === BRAND_PROJECT_STATUSES.PRE_PRODUCTION ||
        statusValue === "Scheduling"
    ) {
        return isInternalUser;
    }

    if (statusValue === BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED || statusValue === "Brief Review") {
        return (
            isInternalUser ||
            (isBrandUser &&
                (project.status === BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED || project.status === "Brief Review"))
        );
    }

    if (
        statusValue === BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED ||
        statusValue === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED ||
        statusValue === "Expert Proposals" ||
        statusValue === "Experts Proposal Review"
    ) {
        return isInternalUser;
    }

    return true;
};

// Who may approve / deny a brief: admin, AM and Fulfillment (post production
// admin). The API accepts every internal role on the brief PATCH.
export const canReviewBrief = (role: string): boolean =>
    ["ADMIN", "ACCOUNT_MANAGER_ADMIN", "POST_PRODUCTION_ADMIN"].includes(String(role));

// Who may approve / deny a proposed roster (TDC-1605): admin, AM, the Expert
// Team (pro team admin) and Fulfillment (post production admin). This is the
// ONLY gate — the project PATCH accepts every internal role — so a role added
// here genuinely gains the action.
export const canReviewExperts = (role: string): boolean =>
    ["ADMIN", "ACCOUNT_MANAGER_ADMIN", "PRO_TEAM_ADMIN", "POST_PRODUCTION_ADMIN"].includes(String(role));

export const isStatusClickable = (project: BrandProject, status: string, role: string, user?: any): boolean => {
    const canAccess = canAccessStatus(project, status, role, user);

    if (status === BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED || status === "Brief Review") {
        return canAccess && canReviewBrief(role);
    }

    return canAccess;
};

export type StatusActionType =
    | "edit"
    | "approval"
    | "cancelled"
    | "scheduling"
    | "expertSelection"
    | "videoReview"
    | "briefReview"
    | "briefDenied"
    | "expertProposals"
    | "briefDialog"
    | "none";

export interface StatusAction {
    type: StatusActionType;
    requiresReview?: boolean;
    showToast?: string;
}

export const getStatusAction = (status: string, role: string): StatusAction => {
    const statusValue = status || "";

    // Cancelled gets its own dialog (reason + Restore Project). Without a branch
    // it would fall through to the briefDialog default — the EDITABLE brief
    // dialog — and submitting a brief writes status='Brief Review' server-side,
    // silently un-cancelling the project.
    if (statusValue === BRAND_PROJECT_STATUSES.CANCELLED || statusValue === "Cancelled") {
        return { type: "cancelled" };
    }

    if (statusValue === BRAND_PROJECT_STATUSES.PENDING || statusValue === "Pending Approval") {
        // Internal users get the approve / not-approve decision dialog; this is
        // the mechanism that replaces hand-picking a status in the edit modal.
        // Brand-side users keep the old edit behaviour — the gate isn't theirs.
        return isInternal(role) ? { type: "approval" } : { type: "edit" };
    }

    if (statusValue === BRAND_PROJECT_STATUSES.SCHEDULING || statusValue === "Scheduling") {
        return { type: "scheduling" };
    }

    if (statusValue === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED || statusValue === "Experts Selection") {
        return { type: "expertSelection" };
    }

    if (statusValue === BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED || statusValue === "Video Review") {
        return { type: "videoReview" };
    }

    if (statusValue === BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED || statusValue === "Brief Review") {
        if (canReviewBrief(role)) {
            return { type: "briefReview" };
        } 
            return {
                type: "none",
                showToast:
                    "The brief is being reviewed by your account manager. You will be notified once it is approved."
            };
        
    }

    if (statusValue === BRAND_PROJECT_STATUSES.BRIEF_DENIED || statusValue === "Brief Denied") {
        return { type: "briefDenied" };
    }

    if (
        statusValue === BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED ||
        statusValue === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED ||
        statusValue === "Expert Proposals" ||
        statusValue === "Experts Proposal Review"
    ) {
        return { type: "expertProposals" };
    }

    if (statusValue === BRAND_PROJECT_STATUSES.EDITING || statusValue === "Editing") {
        return { type: "videoReview" };
    }

    return { type: "briefDialog" };
};

export const getStatusNavigationPath = (project: BrandProject, action: StatusAction): string | null => {
    if (action.type === "videoReview") {
        return `/video-production/projects/${project.id}?tab=video`;
    }

    if (action.type === "expertProposals") {
        return `/video-production/projects/${project.id}?tab=expertProposals`;
    }

    if (action.type === "briefDenied") {
        return `/video-production/projects/${project.id}?tab=details`;
    }

    return null;
};

export const getStatusAccessErrorMessage = (status: string): string => {
    if (
        status === BRAND_PROJECT_STATUSES.SCHEDULING ||
        status === BRAND_PROJECT_STATUSES.PRE_PRODUCTION ||
        status === "Scheduling"
    ) {
        return "You do not have permission to set film dates";
    }
    if (status === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED || status === "Experts Selection") {
        return "You do not have permission to access expert selection";
    }
    if (status === BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED || status === "Brief Review") {
        return "You do not have permission to review briefs";
    }
    if (
        status === BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED ||
        status === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED ||
        status === "Expert Proposals" ||
        status === "Experts Proposal Review"
    ) {
        return "You do not have permission to access expert proposals";
    }
    return "You do not have permission to access this status";
};

const abbreviateName = (name: string): string => {
    const s = String(name || "").trim();
    if (!s) return "BR";
    const words = s.split(/[\s\-/]+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return `${s.replace(/[^a-zA-Z]/g, "").toUpperCase()}XX`.slice(0, 2);
};

const projectBrandName = (project: any): string => {
    const brand = (project?.brands || []).find((b: any) => b?.rmn) || project?.brands?.[0];
    return brand?.abbreviation || brand?.name || project?.product?.title || "";
};

export const formatEntityId = (name: string, id: number | string): string =>
    `${abbreviateName(name)}-${id}`;

export const formatProjectId = (project: any): string =>
    formatEntityId(projectBrandName(project), project?.id ?? "");

export const getAssociationIds = (project: any): string => {
    const ids = [project?.order?.sow?.id, project?.order?.id, project?.id].filter(Boolean);
    if (ids.length === 0) return "";
    return `${abbreviateName(projectBrandName(project))}-${ids.join("-")}`;
};

export type Bpp = {
    confirmation_status?: string | null;
    interested?: boolean | null;
    /** Public share pages carry only this derived flag, never the raw opt state. */
    opted_in?: boolean | null;
    internal_confirmed?: boolean | null;
    user_confirmed?: boolean | null;
    notified_at?: string | null;
    follow_up_count?: number | null;
    created_at?: string | null;
    createdAt?: string | null;
    score?: string | number | null;
    rate?: string | number | null;
    status?: number | null;
    project_bio?: string | null;
    // SOW envelope state (two-envelope flow: expert signs, then the company
    // countersigns). Statuses come from getProjectPros (team views) and the
    // my-projects stitch; the expert envelope id is expert-facing only.
    expert_envelope_id?: string | null;
    expert_signing_status?: string | null;
    internal_signing_status?: string | null;
    // Recorded as already signed at internal confirm (no envelope).
    sow_on_file?: boolean;
};

export type ProDecision = "opted_in" | "opted_out" | "notified" | "pending" | null;

export const getBpp = (expert: any): Bpp | null =>
    expert?.projects?.[0]?.BrandProjectPro
    || expert?.projects?.[0]?.brand_projects_pros
    || expert?.BrandProjectPro
    || expert?.brand_projects_pros
    || null;

export const getProDecision = (bpp: Bpp | null): ProDecision => {
    if (!bpp) return null;
    // interested is the opt-in; opted_in is its public-share copy (the share
    // payload omits interested).
    if (bpp.interested === true || bpp.opted_in === true) return "opted_in";
    if (bpp.interested === false) return "opted_out";
    // "Notified" = pitched (notified_at). internal_confirmed is the later
    // confirm-on-project step; it's kept as a fallback for legacy rows that
    // were pitched before notified_at existed and never answered.
    if (bpp.notified_at || bpp.internal_confirmed === true) return "notified";
    return "pending";
};

export const isProDecided = (bpp: Bpp | null): boolean => {
    const d = getProDecision(bpp);
    return d === "opted_in" || d === "opted_out";
};

export const isProNotified = (bpp: Bpp | null): boolean =>
    getProDecision(bpp) === "notified";

/**
 * Only experts who opted in move past approval: they are the only ones the
 * client ranks, the team schedules, and the API asks to confirm the film date
 * (TDC-1633). Takes a roster row (user carrying projects[0].BrandProjectPro).
 */
export const isProOptedIn = (expert: any): boolean =>
    getProDecision(getBpp(expert)) === "opted_in";

/** Copy shared by the approval surfaces, which still show the whole roster. */
export const optInApprovalNote = (optedIn: number, total: number): string =>
    `All proposed experts are shown here, but only experts who opt in move forward to ranking and scheduling — ${optedIn} of ${total} ${optedIn === 1 ? "has" : "have"} opted in so far.`;

export const formatResponseDate = (raw?: string | null): string | null => {
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

export const adminConfirmationPath = (projectId: number | string, userId: number | string) =>
    `/video-production/projects/${projectId}/experts/${userId}/confirmation`;

// ---------------------------------------------------------------------------
// Shared status phase system
// ---------------------------------------------------------------------------

export type StatusPhase = "inactive" | "active" | "in_progress" | "complete" | "error";

export const STATUS_PHASE_COLORS: Record<StatusPhase, string> = {
    inactive: "#B0B0B0",
    active: "#FFBC00",
    in_progress: "#00C2FF",
    complete: "#1FCE7A",
    error: "#EB3E33",
};

export const getPhaseColor = (phase: StatusPhase): string => STATUS_PHASE_COLORS[phase];

export const STATUS_PHASE_LABELS: Record<StatusPhase, string> = {
    inactive: "Not started",
    active: "Action needed",
    in_progress: "In progress",
    complete: "Complete",
    error: "Needs attention",
};

export const getStatusLabel = (phase: StatusPhase): string => STATUS_PHASE_LABELS[phase];

export interface IconStatus {
    phase: StatusPhase;
    needsAction: boolean;
    tooltip: string;
    verifiedBadge?: boolean;
}

// ---------------------------------------------------------------------------
// Brief icon status
// ---------------------------------------------------------------------------

export const getBriefIconStatus = (project: BrandProject, role: string, user?: any): IconStatus => {
    const brief = project.briefs?.[0];
    const isBrandUser = isBrand(role) || isRMN(user);

    if (!brief) {
        if (isBrandUser) {
            return { phase: "active", needsAction: true, tooltip: "Submit your brief to get started" };
        }
        return { phase: "in_progress", needsAction: false, tooltip: "Waiting for brand to submit a brief" };
    }

    if (brief.status === BRAND_PROJECT_BRIEF_STATUSES.BRIEF_REVIEW_NEEDED) {
        if (isAM(role)) {
            return { phase: "active", needsAction: true, tooltip: "Brief submitted, review and approve to move forward" };
        }
        if (isBrandUser) {
            return { phase: "in_progress", needsAction: false, tooltip: "Your brief has been submitted and is being reviewed" };
        }
        return { phase: "active", needsAction: false, tooltip: "Brief is under review by the account manager" };
    }

    if (brief.status === BRAND_PROJECT_BRIEF_STATUSES.DENIED) {
        if (isBrandUser) {
            return { phase: "error", needsAction: true, tooltip: "Your brief was denied, click to revise and resubmit" };
        }
        return { phase: "error", needsAction: false, tooltip: "Brief was denied, waiting for brand to revise" };
    }

    if (brief.status === BRAND_PROJECT_BRIEF_STATUSES.APPROVED) {
        return { phase: "complete", needsAction: false, tooltip: "Brief approved" };
    }

    return { phase: "inactive", needsAction: false, tooltip: "Brief not started yet" };
};

// ---------------------------------------------------------------------------
// Expert icon status
// ---------------------------------------------------------------------------

export const getExpertIconStatus = (project: BrandProject, role: string, user?: any): IconStatus => {
    const proposalCount = project.project_pros?.length ?? project.project_pros_count ?? 0;
    const isBrandUser = isBrand(role) || isRMN(user);
    const confirmedCount = project.experts_confirmed_count ?? 0;
    const rankedCount = project.experts_ranked_count ?? 0;
    const reviewStatus = project.proposals_review_status ?? "draft";
    const briefApproved = project.briefs?.[0]?.status === BRAND_PROJECT_BRIEF_STATUSES.APPROVED;

    if (project.experts === false) {
        return { phase: "inactive", needsAction: false, tooltip: "No expert required for this project" };
    }

    if (isBrandUser) {
        if (rankedCount > 0) {
            return { phase: "complete", needsAction: false, tooltip: "Experts ranked" };
        }
    } else if (confirmedCount > 0) {
        return { phase: "complete", needsAction: false, tooltip: "Expert confirmed" };
    }

    if (!briefApproved) {
        return { phase: "inactive", needsAction: false, tooltip: "Expert stage not reached yet" };
    }

    if (reviewStatus === "denied") {
        if (isProTeam(role) && !isAM(role)) {
            return { phase: "error", needsAction: true, tooltip: "Expert proposals were rejected, revise and resubmit" };
        }
        if (isAM(role)) {
            return { phase: "error", needsAction: false, tooltip: "Expert proposals denied, pro team is revising" };
        }
        return { phase: "error", needsAction: false, tooltip: "Expert proposals are being revised" };
    }

    if (reviewStatus === "submitted") {
        if (isAM(role)) {
            return { phase: "active", needsAction: true, tooltip: "Expert proposals ready, review and approve to move forward" };
        }
        if (isBrandUser) {
            return { phase: "in_progress", needsAction: false, tooltip: "Experts are being reviewed by your account manager" };
        }
        return { phase: "active", needsAction: false, tooltip: "Expert proposals submitted for AM review" };
    }

    if (reviewStatus === "approved") {
        if (isBrandUser) {
            return { phase: "active", needsAction: true, tooltip: "Your experts are ready, rank and select your top choices" };
        }
        if (rankedCount > 0) {
            if (isAM(role)) {
                return { phase: "active", needsAction: true, tooltip: "Client ranked their experts, confirm an expert to send their SOW" };
            }
            return { phase: "active", needsAction: false, tooltip: "Waiting on expert confirmation" };
        }
        return { phase: "in_progress", needsAction: false, tooltip: "Waiting for client to rank and select experts" };
    }

    if (proposalCount === 0) {
        if (isProTeam(role)) {
            return { phase: "active", needsAction: true, tooltip: "No experts proposed yet, add experts to this project" };
        }
        if (isBrandUser) {
            return { phase: "in_progress", needsAction: false, tooltip: "Experts are being prepared by our team" };
        }
        return { phase: "active", needsAction: false, tooltip: "Experts are being prepared by the pro team" };
    }

    if (isProTeam(role)) {
        return { phase: "active", needsAction: true, tooltip: "Experts proposed, submit them for review" };
    }
    if (isBrandUser) {
        return { phase: "in_progress", needsAction: false, tooltip: "Experts are being prepared by our team" };
    }
    return { phase: "in_progress", needsAction: false, tooltip: "Experts are being confirmed" };
};

// ---------------------------------------------------------------------------
// Shipping icon status
// ---------------------------------------------------------------------------

export const getTdcShippingIconStatus = (project: BrandProject, role: string, user?: any): IconStatus => {
    const status = project.tdc_shipping_status;
    const isBrandUser = isBrand(role) || isRMN(user);

    if (!project.can_ship_two) {
        return { phase: "inactive", needsAction: false, tooltip: "Shipping handled directly with account management" };
    }
    if (!status) {
        return { phase: "inactive", needsAction: false, tooltip: "Shipping not started" };
    }

    switch (status) {
        case BRAND_PROJECT_TDC_SHIPPING_STATUSES.PENDING:
            if (isBrandUser) {
                if (project.tracking) {
                    return { phase: "active", needsAction: false, tooltip: "Tracking submitted, waiting for confirmation from our team" };
                }
                return { phase: "active", needsAction: true, tooltip: "Add your shipping and tracking information" };
            }
            if (project.tracking) {
                return { phase: "active", needsAction: false, tooltip: "Brand submitted tracking, awaiting status update" };
            }
            return { phase: "in_progress", needsAction: false, tooltip: "Shipping information needed from brand" };

        case BRAND_PROJECT_TDC_SHIPPING_STATUSES.SHIPPED:
            if (isBrandUser) {
                return { phase: "in_progress", needsAction: false, tooltip: "Product shipped to TDC, waiting for it to be received" };
            }
            return { phase: "active", needsAction: true, tooltip: "Product shipped, mark as received when it arrives" };

        case BRAND_PROJECT_TDC_SHIPPING_STATUSES.EXCEPTION:
            if (isInternal(role)) {
                return { phase: "error", needsAction: true, tooltip: "Shipping exception, review and resolve" };
            }
            return { phase: "error", needsAction: false, tooltip: "Shipping exception, being handled by our team" };

        case BRAND_PROJECT_TDC_SHIPPING_STATUSES.RECEIVED:
            return { phase: "complete", needsAction: false, tooltip: "Product received by TDC" };

        default:
            return { phase: "inactive", needsAction: false, tooltip: "Shipping not started" };
    }
};

export const getExpertShippingIconStatus = (project: BrandProject, role: string, user?: any): IconStatus => {
    const status = project.expert_shipping_status;
    const isBrandUser = isBrand(role) || isRMN(user);

    if (!project.can_ship_two) {
        return { phase: "inactive", needsAction: false, tooltip: "Shipping handled directly with account management" };
    }

    switch (status) {
        case BRAND_PROJECT_EXPERT_SHIPPING_STATUSES.EXCEPTION:
            if (isInternal(role)) {
                return { phase: "error", needsAction: true, tooltip: "Shipping exception, review and resolve" };
            }
            return { phase: "error", needsAction: false, tooltip: "Shipping exception, being handled by our team" };

        case BRAND_PROJECT_EXPERT_SHIPPING_STATUSES.SHIPPED:
            return { phase: "in_progress", needsAction: false, tooltip: "Product shipped to the expert" };

        case BRAND_PROJECT_EXPERT_SHIPPING_STATUSES.RECEIVED:
            return { phase: "complete", needsAction: false, tooltip: "Product received by the expert" };

        case BRAND_PROJECT_EXPERT_SHIPPING_STATUSES.PENDING:
        default:
            if (project.tdc_shipping_status !== BRAND_PROJECT_TDC_SHIPPING_STATUSES.RECEIVED) {
                return { phase: "inactive", needsAction: false, tooltip: "Waiting for TDC to receive the product" };
            }
            if (isBrandUser) {
                return { phase: "in_progress", needsAction: false, tooltip: "Being shipped to the expert by our team" };
            }
            return { phase: "active", needsAction: true, tooltip: "Product received by TDC, ship it to the expert" };
    }
};

// ---------------------------------------------------------------------------
// Review icon status
// ---------------------------------------------------------------------------

const PRE_REVIEW_STATUSES = [
    BRAND_PROJECT_STATUSES.PENDING,
    BRAND_PROJECT_STATUSES.BRIEF_PENDING,
    BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.BRIEF_DENIED,
    BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED,
    BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED,
    BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED,
    BRAND_PROJECT_STATUSES.SCHEDULING,
    BRAND_PROJECT_STATUSES.PRE_PRODUCTION,
];

export const getReviewIconStatus = (project: BrandProject, role: string, user?: any): IconStatus => {
    const {status} = project;
    const isBrandUser = isBrand(role) || isRMN(user);
    const realFiles = filterRealFiles(project.projectFiles);
    const hasFiles = realFiles.length > 0;
    const allApproved = hasFiles && realFiles.every((f) => f.final === true);

    if (status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED) {
        return { phase: "complete", needsAction: false, tooltip: "Video approved" };
    }

    if (hasFiles) {
        if (allApproved) {
            return { phase: "complete", needsAction: false, tooltip: "Video approved" };
        }
        if (isBrandUser) {
            return { phase: "active", needsAction: true, tooltip: "Your video is ready, click to review and approve" };
        }
        if (isAM(role)) {
            return { phase: "in_progress", needsAction: false, tooltip: "Video delivered, awaiting client review" };
        }
        return { phase: "in_progress", needsAction: false, tooltip: "Video awaiting review" };
    }

    if (PRE_REVIEW_STATUSES.includes(status)) {
        return { phase: "inactive", needsAction: false, tooltip: "Video not started yet" };
    }

    if (status === BRAND_PROJECT_STATUSES.FILMING) {
        return { phase: "in_progress", needsAction: false, tooltip: "Filming in progress" };
    }

    if (status === BRAND_PROJECT_STATUSES.EDITING) {
        if (isPostProd(role)) {
            return { phase: "active", needsAction: true, tooltip: "Video is in post-production, upload when ready" };
        }
        if (isBrandUser) {
            return { phase: "in_progress", needsAction: false, tooltip: "Video is being edited" };
        }
        return { phase: "active", needsAction: false, tooltip: "Video is in post-production, waiting on post prod to upload" };
    }

    if (status === BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED) {
        if (isBrandUser) {
            return { phase: "active", needsAction: true, tooltip: "Your video is ready, click to review and approve" };
        }
        if (isAM(role)) {
            return { phase: "in_progress", needsAction: false, tooltip: "Video delivered, awaiting client review" };
        }
        return { phase: "in_progress", needsAction: false, tooltip: "Video awaiting review" };
    }

    return { phase: "inactive", needsAction: false, tooltip: "Video not started yet" };
};

export const getProductTestingExpertCount = (project: BrandProject): number =>
    project.product_testing_experts_count || 0;

export const showProductTestingIcon = (project: BrandProject, role: string): boolean =>
    isInternal(role) && getProductTestingExpertCount(project) > 0;

export const getProductTestingIconStatus = (project: BrandProject): IconStatus => {
    const experts = getProductTestingExpertCount(project);
    return {
        phase: "complete",
        needsAction: false,
        tooltip: `${experts} ${experts === 1 ? "expert" : "experts"} submitted product feedback, click to view (read only)`,
    };
};

// ---------------------------------------------------------------------------
// Download icon status
// ---------------------------------------------------------------------------

export const getDownloadIconStatus = (project: BrandProject, role: string, user?: any): IconStatus => {
    const realFiles = filterRealFiles(project.projectFiles);
    const hasVideos = realFiles.length > 0;
    const isBrandUser = isBrand(role) || isRMN(user);
    const projectFinished = project.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED;
    const canDownload =
        isInternal(role) ||
        !!project.brands?.some((b) => b.pre_approval_downloading) ||
        (isBrandUser && projectFinished);
    const allApproved = hasVideos && realFiles.every((f) => f.final === true);

    if (!canDownload || !hasVideos) {
        return { phase: "inactive", needsAction: false, tooltip: "No downloads available yet" };
    }

    if (isBrandUser) {
        if (allApproved) {
            return { phase: "complete", needsAction: false, verifiedBadge: true, tooltip: "Your final videos are ready to download" };
        }
        return { phase: "complete", needsAction: false, tooltip: "Videos available for download" };
    }

    if (allApproved) {
        return { phase: "complete", needsAction: false, verifiedBadge: true, tooltip: "All videos approved, available for download" };
    }
    return { phase: "complete", needsAction: false, tooltip: "Videos available for download" };
};

export interface VideoCounts {
    /** Delivered videos — files with an uploaded key */
    total: number;
    approved: number;
    /** Full deliverable count — empty slots + delivered videos */
    deliverableTotal: number;
}

export const filterRealFiles = <T extends { key?: string | null }>(
    files?: T[] | null,
): T[] => (files ?? []).filter((f) => f.key != null && f.key !== "");

export const getVideoCounts = (project?: BrandProject | null): VideoCounts => {
    const allFiles = project?.projectFiles ?? [];
    const realFiles = filterRealFiles(allFiles);
    return {
        total: realFiles.length,
        approved: realFiles.filter((f) => f.final === true).length,
        deliverableTotal: allFiles.length,
    };
};

export interface ExpertCounts {
    proposed: number;
    optedIn: number;
    optedOut: number;
    confirmed: number;
    hasDecisionData: boolean;
}

// NOTE: experts_*_count and project_pros are PER-PROCESS aggregates now (the
// project's experts come from its shared brand_order_pros). Two projects that
// share a process therefore report identical counts here — don't reintroduce a
// project-local assumption.
export const getExpertCounts = (project: BrandProject): ExpertCounts => {
    const pros = project.project_pros ?? [];
    const proposed = pros.length || project.project_pros_count || 0;

    if (project.experts_opted_in_count != null || project.experts_opted_out_count != null) {
        return {
            proposed: project.experts_proposed_count ?? proposed,
            optedIn: project.experts_opted_in_count ?? 0,
            optedOut: project.experts_opted_out_count ?? 0,
            confirmed: project.experts_confirmed_count ?? 0,
            hasDecisionData: true,
        };
    }

    let optedIn = 0;
    let optedOut = 0;
    let confirmed = 0;
    let hasDecisionData = false;
    pros.forEach((pp) => {
        const bpp = getBpp(pp);
        if (!bpp) return;
        hasDecisionData = true;
        const decision = getProDecision(bpp);
        if (decision === "opted_in") optedIn += 1;
        if (decision === "opted_out") optedOut += 1;
        if (bpp.internal_confirmed === true) confirmed += 1;
    });

    return { proposed, optedIn, optedOut, confirmed, hasDecisionData };
};

const REOPEN_SELECTION_STEPS = [
    "Submit for review",
    "Approve",
    "Re-rank",
    "Set filming range",
    "Schedule",
];

// Removal keeps the approved roster — nothing to re-propose, only the shoot
// dates need re-confirming (the API unwinds to Scheduling, not Expert
// Proposals; see removeFromProjectPros).
const REOPEN_SELECTION_STEPS_REMOVE = ["Schedule"];

export const reopenSelectionWarning = (
    expertName: string,
    siblingProjects: Array<{ id: number; title?: string }> = [],
    action: "add" | "remove" = "add",
) => {
    const scope =
        siblingProjects.length > 0
            ? `this project and ${describeSharedSiblings(siblingProjects)}`
            : "this project";
    if (action === "remove") {
        return {
            headline: `Removing ${expertName} sends this selection back to Scheduling`,
            detail: `Sends ${scope} back to Scheduling. The SOWs already sent are invalidated, and every expert's date confirmation is cleared.`,
            steps: REOPEN_SELECTION_STEPS_REMOVE,
        };
    }
    return {
        headline: `Adding ${expertName} reopens this selection`,
        detail: `Sends ${scope} back to Expert Proposals. The SOWs already sent are invalidated, and every expert's date confirmation is cleared.`,
        steps: REOPEN_SELECTION_STEPS,
    };
};

export const SHOT_SELECTION_STATUSES: string[] = [
    BRAND_PROJECT_STATUSES.EDITING,
    BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED,
    BRAND_PROJECT_STATUSES.FINAL_DELIVERED,
];

export const expertDisplayName = (expert: any): string =>
    expert?.displayName || `Expert ${expert?.userid}`;

export const formatExpertCountSummary = (counts: ExpertCounts): string => {
    if (!counts.hasDecisionData) {
        return `${counts.proposed} proposed`;
    }
    return `${counts.proposed} proposed, ${counts.optedIn} opted in, ${counts.optedOut} opted out, ${counts.confirmed} of ${counts.proposed} confirmed`;
};

export const formatVideoCountSummary = (counts: VideoCounts): string =>
    `${counts.approved} of ${counts.total} approved`;
